import { useState } from "react";
import { useLogin } from "@refinedev/core";
import { Form, Input, Button, Card, Typography, Alert, theme } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";
import { MfaVerifyForm } from "../../components/auth/MfaVerifyForm";
import { APP_CONFIG } from "../../config/app";
import { gradients } from "../../config/theme";
import { useTheme } from "../../contexts/ThemeContext";

const { Title, Text } = Typography;

interface LoginFormValues {
  username: string;
  userPass: string;
}

type LoginStep = "password" | "mfa";

export const Login: React.FC = () => {
  const { mutate: login, isLoading } = useLogin();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const { isDark } = useTheme();
  const { token } = theme.useToken();

  // MFA state
  const [step, setStep] = useState<LoginStep>("password");
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);

  const onFinish = (values: LoginFormValues) => {
    setError(null);
    login(
      { username: values.username, password: values.userPass },
      {
        onSettled: (data) => {
          if (data && data.success === false) {
            // Check for MFA challenge before showing generic error (Issue #7)
            if (data.error?.name === "mfa_required") {
              setChallengeToken(data.error.message);
              setStep("mfa");
              return;
            }
            setError(t("auth.invalidCredentials"));
          }
        },
        onError: () => {
          setError(t("auth.invalidCredentials"));
        },
      },
    );
  };

  const handleMfaVerify = (code: string, trustDevice: boolean) => {
    if (!challengeToken) return;

    setMfaError(null);
    setMfaLoading(true);

    login(
      {
        mfaChallengeToken: challengeToken,
        mfaCode: code,
        trustDevice,
      },
      {
        onSettled: (data) => {
          setMfaLoading(false);
          if (data && data.success === false) {
            setMfaError(t("auth.mfa.invalidCode"));
          }
        },
        onError: (err) => {
          setMfaLoading(false);
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          if (status === 410) {
            setMfaError(t("auth.mfa.codeExpired"));
            setTimeout(() => handleMfaBack(), 2000);
          } else if (status === 429) {
            setMfaError(t("auth.mfa.tooManyAttempts"));
            setTimeout(() => handleMfaBack(), 2000);
          } else {
            setMfaError(t("auth.mfa.invalidCode"));
          }
        },
      },
    );
  };

  const handleMfaBack = () => {
    setStep("password");
    setChallengeToken(null);
    setMfaError(null);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: isDark ? gradients.loginDark : gradients.loginLight,
      }}
    >
      <div style={{ position: "absolute", top: 16, right: 16 }}>
        <LanguageSwitcher />
      </div>
      <Card
        style={{
          maxWidth: 400,
          width: "100%",
          boxShadow: isDark
            ? "0 8px 32px rgba(0, 0, 0, 0.5)"
            : "0 8px 32px rgba(99, 102, 241, 0.15)",
          backdropFilter: isDark ? "blur(12px)" : undefined,
          background: isDark
            ? "rgba(30, 41, 59, 0.85)"
            : token.colorBgContainer,
          border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
        }}
      >
        {step === "mfa" && challengeToken ? (
          <MfaVerifyForm
            isLoading={mfaLoading}
            error={mfaError}
            onVerify={handleMfaVerify}
            onBack={handleMfaBack}
          />
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              {APP_CONFIG.appLogo && (
                <img
                  src={APP_CONFIG.appLogo}
                  alt="Logo"
                  style={{ height: "80px", marginBottom: "16px" }}
                />
              )}
              <Title
                level={2}
                style={{
                  color: token.colorPrimary,
                  marginBottom: 8,
                  marginTop: 0,
                }}
              >
                {APP_CONFIG.appName}
              </Title>
              <Text type="secondary">{t("auth.signInToAccount")}</Text>
            </div>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: 16 }}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{ username: "", userPass: "" }}
            >
              <Form.Item
                name="username"
                label={t("auth.username")}
                rules={[
                  { required: true, message: t("auth.usernameRequired") },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder={t("auth.enterUsername")}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="userPass"
                label={t("auth.userPass")}
                rules={[{ required: true, message: t("auth.passRequired") }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder={t("auth.enterPass")}
                  size="large"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={isLoading}
                >
                  {t("auth.signIn")}
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Card>
    </div>
  );
};
